/**
 * Map Configuration — Shared constants for Map View, SchedulerV3, and other visualisations
 *
 * Centralises job type colors, abbreviations, and tech avatar colors so they stay
 * consistent across the scheduler, map view, and any future data-viz features.
 */

// ═══════════════════════════════════════
// JOB TYPE COLORS
// ═══════════════════════════════════════

export const TYPE_COLORS: Record<string, { bg: string; light: string; text: string }> = {
  Install: { bg: '#7C3AED', light: '#EDE9FE', text: '#6D28D9' },
  Fault: { bg: '#E11D48', light: '#FFF1F2', text: '#BE123C' },
  'Pre-Site': { bg: '#D97706', light: '#FEF3C7', text: '#B45309' },
  Maintenance: { bg: '#2563EB', light: '#DBEAFE', text: '#1D4ED8' },
  Task: { bg: '#64748B', light: '#F1F5F9', text: '#475569' },
};

export const TYPE_ABBREV: Record<string, string> = {
  Install: 'INS',
  Fault: 'FLT',
  'Pre-Site': 'PRE',
  Maintenance: 'MNT',
  Task: 'TSK',
};

// ═══════════════════════════════════════
// TECH AVATAR COLORS (cycled by index)
// ═══════════════════════════════════════

export const AVATAR_COLORS = [
  '#7C3AED', // Purple
  '#2563EB', // Blue
  '#059669', // Green
  '#D97706', // Orange
  '#E11D48', // Red
  '#8B5CF6', // Violet
  '#0891B2', // Cyan
  '#65A30D', // Lime
  '#CA8A04', // Amber
  '#DC2626', // Crimson
];

export function getAvatarColor(index: number): string {
  return AVATAR_COLORS[index % AVATAR_COLORS.length];
}

// ═══════════════════════════════════════
// MAP DEFAULTS
// ═══════════════════════════════════════

/**
 * Office location — re-export from the canonical source. Default is
 * Techery's Bowral head office; per-tenant overrides live at
 * `tenant.business.officeLocation` and are merged in by
 * `getOfficeLocation(tenant)`. These re-exports preserve the existing
 * import shape for back-compat — callers without tenant context still
 * see the defaults.
 *
 * TODO(saas-readiness): callers should switch to `getOfficeLocation(tenant)` /
 * `getDefaultMapCenter(tenant)` / `getJobFallbackCoords(tenant)` so
 * per-tenant overrides take effect. Without tenant context, the map
 * office marker shows Techery's name + Bong Bong St address to every
 * tenant — customer-facing leak. Tracked as a follow-up to the
 * SaaS-readiness audit (May 2026).
 */
import { DEFAULT_OFFICE_LOCATION } from '@/utils/officeLocation';
export const OFFICE_LOCATION = DEFAULT_OFFICE_LOCATION;
export const DEFAULT_MAP_CENTER = { lat: OFFICE_LOCATION.lat, lng: OFFICE_LOCATION.lng };
export const JOB_FALLBACK_COORDS = { lat: OFFICE_LOCATION.lat, lng: OFFICE_LOCATION.lng };

/** Default zoom level — covers ~30km radius (Bowral/Mittagong/Moss Vale) */
export const DEFAULT_MAP_ZOOM = 11;

/** Stale threshold — kept for back-compat. No longer used to hide markers:
 *  people now persist on the map at their last-seen position forever. */
export const LOCATION_STALE_MS = 10 * 60 * 1000; // 10 minutes

/** Recent threshold — locations within this show at full opacity + "online" (ms) */
export const LOCATION_RECENT_MS = 5 * 60 * 1000; // 5 minutes

/** Offline threshold — beyond this a person is shown greyed as "last seen X ago",
 *  but is NEVER removed from the map (ms) */
export const LOCATION_OFFLINE_MS = 30 * 60 * 1000; // 30 minutes

/** Heartbeat throttle — while the app is open but the user is stationary, refresh
 *  `liveLocation.lastSeen` at most this often so they stay "online" without a
 *  full position re-write (ms) */
export const LOCATION_HEARTBEAT_MS = 2 * 60 * 1000; // 2 minutes

/** GPS polling interval (ms) */
export const GPS_POLL_INTERVAL_MS = 30_000; // 30 seconds

/** Minimum distance change before writing to Firestore (meters) */
export const MIN_DISTANCE_CHANGE_M = 50;

/** Radius (meters) for auto on-site arrival detection */
export const ARRIVAL_RADIUS_M = 150;

/** Re-check delay (ms) after tech taps "Not Yet" on arrival prompt */
export const ARRIVAL_SNOOZE_MS = 120_000; // 2 minutes

/** Radius (meters) beyond which a tech is considered to have LEFT the job
 *  site. Wider than ARRIVAL_RADIUS_M on purpose so a tech walking out to
 *  their ute on a big site doesn't trigger a departure prompt. */
export const DEPARTURE_RADIUS_M = 300;

/** How long the tech must be continuously outside DEPARTURE_RADIUS_M
 *  before we surface the "did you leave?" prompt. Buys back the case
 *  where the tech walks to a far corner of the property and comes back. */
export const DEPARTURE_MIN_DURATION_MS = 3 * 60_000; // 3 minutes

/** Re-check delay (ms) after tech taps "Still on site" — pauses
 *  departure detection for this job for the snooze window. */
export const DEPARTURE_SNOOZE_MS = 5 * 60_000; // 5 minutes

// ═══════════════════════════════════════
// GOOGLE MAPS DARK STYLE
// ═══════════════════════════════════════

export const MAP_ID_LIGHT = ''; // Use default roadmap
export const MAP_ID_DARK = ''; // Use default + dark styles below

export const DARK_MAP_STYLES: google.maps.MapTypeStyle[] = [
  { elementType: 'geometry', stylers: [{ color: '#1d2c4d' }] },
  { elementType: 'labels.text.fill', stylers: [{ color: '#8ec3b9' }] },
  { elementType: 'labels.text.stroke', stylers: [{ color: '#1a3646' }] },
  { featureType: 'administrative.country', elementType: 'geometry.stroke', stylers: [{ color: '#4b6878' }] },
  { featureType: 'administrative.province', elementType: 'geometry.stroke', stylers: [{ color: '#4b6878' }] },
  { featureType: 'landscape', elementType: 'geometry', stylers: [{ color: '#1a2744' }] },
  { featureType: 'poi', elementType: 'geometry', stylers: [{ color: '#283d6a' }] },
  { featureType: 'poi', elementType: 'labels.text.fill', stylers: [{ color: '#6f9ba5' }] },
  { featureType: 'poi.park', elementType: 'geometry.fill', stylers: [{ color: '#1a3541' }] },
  { featureType: 'road', elementType: 'geometry', stylers: [{ color: '#304a7d' }] },
  { featureType: 'road', elementType: 'labels.text.fill', stylers: [{ color: '#98a5be' }] },
  { featureType: 'road.highway', elementType: 'geometry', stylers: [{ color: '#2c6675' }] },
  { featureType: 'road.highway', elementType: 'geometry.stroke', stylers: [{ color: '#255763' }] },
  { featureType: 'transit', elementType: 'labels.text.fill', stylers: [{ color: '#98a5be' }] },
  { featureType: 'water', elementType: 'geometry', stylers: [{ color: '#0e1626' }] },
  { featureType: 'water', elementType: 'labels.text.fill', stylers: [{ color: '#4e6d70' }] },
];
